import React, { useEffect, useState } from 'react'
import { TouchableOpacity, View, ViewStyle, TextStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { Dialog, DialogActions, DialogContent } from '@mui/material'

import { useStores } from '@trivie/core'
import { color, iconChevronRight, SvgIcon, Text, Button } from '@trivie/ui-mobile'
import { useHistory } from 'react-router-dom'

export const SettingsList = observer(() => {
  const { userStore, subscriptionStore } = useStores()

  const { accessCode, logout, user } = userStore

  const hasActive = subscriptionStore.hasActiveSubscriptions

  const history = useHistory()

  const goToAccountScreen = () => history.push('settings/account')
  const goToNotificationsScreen = () => history.push('settings/notifications')
  const goToSoundMusicScreen = () => history.push('settings/audio')
  const goToLanguageScreen = () => history.push('settings/language')
  const goToReportProblem = () => history.push('settings/report')
  const goToGDPRSettings = () => history.push('settings/gdpr')

  const goToOrganizationPolicy = () => {
    setShowModal(user?.workspace_settings?.policy && !showModal)
  }

  const gdpr_enabled = user?.workspace_settings?.gdpr_enabled

  const [showModal, setShowModal] = useState<boolean | undefined>(false)
  const [items] = useState([
    {
      label: 'Settings.Account.header',
      onPress: goToAccountScreen,
      display: true,
    },
    {
      label: 'Notifications.header',
      onPress: goToNotificationsScreen,
      display:
        !userStore.isSubscriptionWorkspace || (userStore.isSubscriptionWorkspace && hasActive),
    },
    {
      label: 'Settings.Language',
      onPress: goToLanguageScreen,
      display:
        !userStore.isSubscriptionWorkspace || (userStore.isSubscriptionWorkspace && hasActive),
    },
    {
      label: 'Settings.Sounds.header',
      onPress: goToSoundMusicScreen,
      display:
        !userStore.isSubscriptionWorkspace || (userStore.isSubscriptionWorkspace && hasActive),
    },
    {
      label: 'Settings.TOS',
      onPress: () => window.open('https://www.trivie.com/terms-of-service', '_blank'),
      display: true,
    },
    {
      label: 'Settings.PrivacyPolicy',
      onPress: () => window.open('https://www.trivie.com/privacy', '_blank'),
      display: true,
    },
    {
      label: 'Settings.gdpr',
      onPress: goToGDPRSettings,
      display: gdpr_enabled,
    },
    {
      label: 'Settings.OrganizationPolicy',
      onPress: goToOrganizationPolicy,
      display: user?.workspace_settings?.policy,
    },
    {
      label: 'Settings.ReportProblem',
      onPress: goToReportProblem,
      display:
        !userStore.isSubscriptionWorkspace || (userStore.isSubscriptionWorkspace && hasActive),
    },
  ])

  useEffect(() => {
    if (user) {
      userStore.getUser(user.id)
    }
  }, [])

  const renderItem = (item: {
    label: string
    onPress: () => void
    display: boolean | undefined
  }) => (
    <View style={LIST_ITEM_CONTAINER}>
      <TouchableOpacity
        // @ts-ignore
        dataSet={{ qat: `settings_item_${item.label}` }}
        onPress={item.onPress}
        style={LIST_ITEM_BTN}
      >
        <View style={LIST_ITEM}>
          <Text variant="label" tx={item.label} />
          <SvgIcon src={iconChevronRight} color={color.shade30} size={24} />
        </View>
      </TouchableOpacity>
    </View>
  )

  return (
    <>
      {items.filter((f) => f.display).map((item) => renderItem(item))}
      <TouchableOpacity
        onPress={() => {
          logout()
          history.replace('/login')
        }}
        style={{ flexDirection: 'row' }}
      >
        <View style={LIST_ITEM}>
          <Text style={LOGOUT} variant="label" tx="Onboarding.Login.logOut" />
        </View>
      </TouchableOpacity>

      <View style={INFO}>
        <Text
          style={VERSION}
          tx="Settings.Version"
          txOptions={{ versionNum: process.env.REACT_APP_VERSION }}
          variant="titleEmphasized"
        />
        <Text
          style={WORKSPACE}
          tx="Settings.WorkspaceCode"
          txOptions={{ code: accessCode }}
          variant="titleEmphasized"
        />
      </View>

      <Dialog maxWidth="xs" aria-labelledby="confirmation-dialog-title" open={showModal || false}>
        <div style={DIALOG_TITLE_CONTAINER}>
          <Text style={{ fontSize: 18, fontWeight: 'bold' }} tx="Settings.OrganizationPolicy" />
        </div>

        <DialogContent style={DIALOG_CONTENT}>
          <div style={BODY_TEXT_WRAPPER}>
            <Text text={user?.workspace_settings?.policy_text} />
          </div>
        </DialogContent>
        <DialogActions style={DIALOG_BUTTON_CONTAINER}>
          <Button
            accessibilityRole="button"
            accessibilityLabel="Confirm"
            style={{ borderRadius: 15, padding: 25 }}
            onPress={() => setShowModal(false)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
})

/**
 * Styling
 */
const LIST_ITEM_CONTAINER: ViewStyle = {
  borderBottomWidth: 1,
  borderBottomColor: color.shade10,
}
const LIST_ITEM: ViewStyle = {
  flexDirection: 'row',
  flex: 1,
  justifyContent: 'space-between',
  alignContent: 'center',
  paddingVertical: 20,
}
const INFO: ViewStyle = {
  flexDirection: 'column',
  flex: 1,
  justifyContent: 'flex-end',
  alignItems: 'center',
  alignContent: 'center',
  paddingVertical: 20,
}

const LIST_ITEM_BTN: ViewStyle = { flexDirection: 'row' }

const VERSION: TextStyle = {
  marginBottom: 10.5,
  fontSize: 12.5,
  fontWeight: '400',
  color: '#808080',
}

const LOGOUT: TextStyle = { color: color.red }

const WORKSPACE: TextStyle = {
  marginBottom: 15.5,
  fontSize: 12.5,
  fontWeight: '400',
  color: '#808080',
}

const DIALOG_TITLE_CONTAINER: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 20,
  paddingTop: 25,
  paddingBottom: 25,
}

const DIALOG_BUTTON_CONTAINER: React.CSSProperties = {
  display: 'block',
  color: color.white,
  margin: 20,
  marginBottom: 25,
}
const BODY_TEXT_WRAPPER: React.CSSProperties = {
  maxHeight: 500,
  paddingRight: 10,
  overflowY: 'auto',
  textAlign: 'left',
  fontSize: 15,
  letterSpacing: 0.3,
}
const DIALOG_CONTENT: React.CSSProperties = {
  paddingLeft: 30,
  paddingRight: 30,
}
